import { BasisPoints } from '@klear/basis-points'
import { Money } from '@klear/money'
import { KlearDate } from '@klear/date'
import type { FeeEntityType } from '@klear/constants'
import type { CurrencyCode } from '@klear/constants/currency'

export type FeeIdentity = { kind: 'new' } | { kind: 'saved'; id: number }

export type FeeJson = {
  identity: FeeIdentity
  orderId: number
  parentId: number
  idDisplay?: string
  type: FeeEntityType
  isManuallyPriced?: boolean
  effectiveDate?: string
  endDate?: string
  ratingUsed?: string
  ratingAgency?: string
  tierUsed?: string
  notes?: string
  klearBeneficiaryId: number
  totalAmount: ReturnType<Money['toJSON']>
  totalRateUsed: number
  spvBeneficiaryId: number
  spvAmount: ReturnType<Money['toJSON']>
  spvRateUsed: number
  createdAt?: number
  updatedAt?: number
  deletedAt?: number
  createdByUserId?: number
  createdByUserEmail?: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  deletedByUserId?: number
  deletedByUserEmail?: string
}

export function feeToJson(fee: Fee): FeeJson {
  return {
    identity: fee.identity,
    orderId: fee.orderId,
    parentId: fee.parentId,
    idDisplay: fee.idDisplay,
    type: fee.type,
    isManuallyPriced: fee.isManuallyPriced,
    effectiveDate: fee.effectiveDate?.toJSON(),
    endDate: fee.endDate?.toJSON(),
    ratingUsed: fee.ratingUsed,
    ratingAgency: fee.ratingAgency,
    tierUsed: fee.tierUsed,
    notes: fee.notes,
    klearBeneficiaryId: fee.klearBeneficiaryId,
    totalAmount: fee.totalAmount.toJSON(),
    totalRateUsed: fee.totalRateUsed.value,
    spvBeneficiaryId: fee.spvBeneficiaryId,
    spvAmount: fee.spvAmount.toJSON(),
    spvRateUsed: fee.spvRateUsed.value,
    createdAt: fee.createdAt,
    updatedAt: fee.updatedAt,
    deletedAt: fee.deletedAt,
    createdByUserId: fee.createdByUserId,
    createdByUserEmail: fee.createdByUserEmail,
    updatedByUserId: fee.updatedByUserId,
    updatedByUserEmail: fee.updatedByUserEmail,
    deletedByUserId: fee.deletedByUserId,
    deletedByUserEmail: fee.deletedByUserEmail
  }
}

export class Fee {
  static readonly __klearClass = 'Fee'

  readonly identity: FeeIdentity
  orderId: number
  parentId: number
  idDisplay?: string
  type: FeeEntityType
  isManuallyPriced: boolean
  effectiveDate?: KlearDate
  endDate?: KlearDate
  ratingUsed?: string
  ratingAgency?: string
  tierUsed?: string
  notes?: string
  klearBeneficiaryId: number
  totalAmount: Money
  totalRateUsed: BasisPoints
  spvBeneficiaryId: number
  spvAmount: Money
  spvRateUsed: BasisPoints
  createdAt?: number
  updatedAt?: number
  deletedAt?: number
  createdByUserId?: number
  createdByUserEmail?: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  deletedByUserId?: number
  deletedByUserEmail?: string

  constructor(props: FeeJson) {
    this.identity = props.identity?.kind === 'saved' ? { kind: 'saved', id: props.identity.id } : { kind: 'new' }
    this.orderId = props.orderId
    this.parentId = props.parentId
    this.idDisplay = props.idDisplay
    this.type = props.type
    this.isManuallyPriced = props.isManuallyPriced ?? false
    this.effectiveDate = KlearDate.parse(props.effectiveDate)
    this.endDate = KlearDate.parse(props.endDate)
    this.ratingUsed = props.ratingUsed
    this.ratingAgency = props.ratingAgency
    this.tierUsed = props.tierUsed
    this.notes = props.notes
    this.klearBeneficiaryId = props.klearBeneficiaryId
    this.totalAmount = new Money(props.totalAmount)
    this.totalRateUsed = new BasisPoints(props.totalRateUsed)
    this.spvBeneficiaryId = props.spvBeneficiaryId
    this.spvAmount = new Money(props.spvAmount)
    this.spvRateUsed = new BasisPoints(props.spvRateUsed)
    this.createdAt = props.createdAt
    this.updatedAt = props.updatedAt
    this.deletedAt = props.deletedAt
    this.createdByUserId = props.createdByUserId
    this.createdByUserEmail = props.createdByUserEmail
    this.updatedByUserId = props.updatedByUserId
    this.updatedByUserEmail = props.updatedByUserEmail
    this.deletedByUserId = props.deletedByUserId
    this.deletedByUserEmail = props.deletedByUserEmail
    this.assertValid()
  }

  get id(): number | undefined {
    return this.identity.kind === 'saved' ? this.identity.id : undefined
  }

  get isSaved(): boolean {
    return this.identity.kind === 'saved'
  }

  get amount(): Money {
    return this.totalAmount
  }

  get rateUsed(): BasisPoints {
    return this.totalRateUsed
  }

  get klearAmount(): Money {
    return this.totalAmount.subtract(this.spvAmount)
  }

  get klearRateUsed(): BasisPoints {
    return BasisPoints.signed(this.totalRateUsed.value - this.spvRateUsed.value)
  }

  get currency(): CurrencyCode {
    return this.amount.currency
  }

  requireSaved(): asserts this is this & { id: number } {
    if (!this.isSaved) {
      throw new Error('Fee is not saved')
    }
  }

  assertValid(): void {
    if (!this.orderId) throw new Error('Fee orderId is required')
    if (this.parentId == null) throw new Error('Fee parentId is required')
    if (this.totalAmount.currency !== this.spvAmount.currency) {
      throw new Error('Fee total and SPV portion must share a single currency')
    }
  }

  toJSON(): FeeJson {
    return feeToJson(this)
  }
}
